import React from 'react';
import { HeartPulse } from 'lucide-react';
import type { WeeklyPlanningReportModel } from '../kit/useWeeklyPlanningReport';
import { benchmarkIndex, benchmarkMonthEnd, CellContext, coverCap, fmtCurrency, fmtPct, fmtWeeks, formatIndex, gatedPeerValue, indexTone, inventoryRetailOf, isNum, isPositive, LY_BOM_MISSING_KPI, LY_BOM_TOOLTIP, LY_BOM_WEEKLY_TOOLTIP, lyBomMissing, lyBomOf, NOT_AT_COST_TOOLTIP, pctChange, PEER_TEXT_NOT_ENOUGH, ratioPct, retailTag, retailTooltip, signedPct, sumCells, basisText, bomAbbr, bomInventoryLabel, coverUnit, EMPTY, fmtCover, inventoryEndOnBasis, LY_BOM_MISSING_NOTE, metricById, resolveTarget, v2HistoryNote, agingShown, benchmarkForRows, BenchmarkView, chartRowsByClass, chartRowsByClassWithPeers, kpiTotals, ownIn, peerIn, peerMarkerNote, totalRows, weightedCover, withPeers, salesAhead, salesAheadPeriods, FlatRow, totalsNoun, chainChildRows } from '../kit/logic';
import { type ReportPageConfig, WeeklyPlanningReportPage, SegmentedControl, benchmarkDashboardCard, ChartCard, ChartSeries, BenchmarkDetail, SalesAheadChart, DENSE_TABLE_CSS, DENSE_TD_CSS, DENSE_TH_CSS, DetailCard, FamilyDrivers, ReorderDetail, ShowAllRows, Stat, StatStrip, type KpiBenchmarkLine, type KpiItem, ESTIMATED_TOOLTIP } from '../kit/ui';
import ReportModal from '../ReportModal.component';
import { AnyRow, ClassRow, Freshness, isVendorRow } from '../../../interfaces/reports/WeeklyPlanning.interface';
import { coverVsTarget, DETAIL_CHART_HEIGHT, DETAIL_CHART_WIDTH, summaryStats } from '../weeklyAction/WeeklyActionTableReport.component';
import { cn } from '../../../lib/utils';
// Inventory Health's expanded-row panel (spec 7.1 "Expanded row: inventory summary, breakdown by
// location, aging bar (A/B)"). Everything comes from the row's own payload data:
// - Inventory: start and end of the target period (cost cells on the cost basis where the row has
// them), plan, vs plan, cover vs target with its basis, last year's BOM inventory when the payload has it, and the
// estimated note when the target-period cell is estimated. vs Plan and vs LY compare the RETAIL
// start inventory (plans and LY are retail) whatever basis the start / end figures display.
// - Aging (the kit's `agingShown`: A/B with inflows; the Freshness switch does not hide it): one stacked
// 0–4w / 5–8w / 8w+ bar from the row's `freshness` dollars as shares of its on-hand dollars. Class rows carry the buckets rolled up
// from their SKUs by the backend (FreshnessService::classRollup), so no SKU load is needed; a class with no SKU stock says "No SKU stock to age".
// Product rows are vendor-shaped (`/product-rows`) and carry the freshness rolled up from their variants.
// - By location: only in the Location view, every row of the expandAll tree with this class code
// (one per location, no cut-off); other views show no breakdown rather than an invented split.
// - Cost basis: the dollars are the table's own cells (`metricById(id).cell`) or `basis.ts`, so start, end,
// plan, LY BOM and the location inventory follow the basis or read "—" (no cost figure); the aging dollars are retail
// (freshness carries retail only), so on the cost basis the bar shows the shares alone. Ratios stay retail.
// -: the inventory summary is one stat strip (BOM inventory · End of period · Plan
// BOM · vs Plan · WOS / MOS vs target · LY BOM · On order · Inflows to date), long explanations in each stat's
// tooltip (the estimated note on BOM inventory).: Inventory Health is the stock-position report, so the
// strip carries no OTB, OTB + Dev or Trended sales stat (Weekly Action says what to buy); Aging, "Sales ahead: plan vs LY" and the Benchmark card sit side by side; the child
// drivers and the location breakdown are dense tables that fold long lists behind "Show all". The parts are Weekly Action's.



const BUCKETS: { key: keyof Pick<Freshness, 'aging4wRetail' | 'aging8wRetail' | 'aging12wRetail'>; label: string; color: string }[] = [
  // the one set of bucket labels (the Age 0–4w / 5–8w / 8w+ columns, the Inflow timing chart and the KPI say the same).
  { key: 'aging4wRetail', label: '0–4w', color: 'bg-[#12B76A]' },
  { key: 'aging8wRetail', label: '5–8w', color: 'bg-[#FDB022]' },
  { key: 'aging12wRetail', label: '8w+', color: 'bg-[#F04438]' },
];

export const AgingBar: React.FC<{ freshness: Freshness; showDollars?: boolean }> = ({ freshness, showDollars = true }) => {
  // Shares of on-hand dollars (as the aging columns); the bucket sum only when on-hand is not positive.
  const bucketSum = BUCKETS.reduce((a, b) => a + freshness[b.key], 0);
  const total = isPositive(freshness.onHandRetail) ? freshness.onHandRetail : bucketSum;
  if (!isPositive(total)) return <p className="text-[12px] font-medium text-[#667085]">No stock on hand to age.</p>;
  const parts = BUCKETS.map((b) => ({ ...b, dollars: freshness[b.key], share: freshness[b.key] / total }));
  const name = `Aging: ${parts.map((p) => (showDollars ? `${p.label} ${fmtPct(p.share)} (${fmtCurrency(p.dollars)})` : `${p.label} ${fmtPct(p.share)}`)).join(', ')}`;
  return (
    <div className="flex flex-col gap-1.5">
      <div role="img" aria-label={name} className="flex h-3 w-full overflow-hidden rounded-full bg-[#F2F4F7]">
        {parts.map((p) => (
          <div key={p.key} className={p.color} style={{ width: `${p.share * 100}%` }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-medium text-[#475467]" aria-hidden="true">
        {parts.map((p) => (
          <li key={p.key} className="flex items-center gap-1.5 tabular-nums">
            <span className={`inline-block w-2 h-2 rounded-full ${p.color}`} />
            {p.label} {fmtPct(p.share)}{showDollars ? ` · ${fmtCurrency(p.dollars)}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * W4 (item 15): receipt timing. On-hand inventory (retail dollars) by how long ago it was received, as small vertical bars over the
 * same buckets as the Aging bar: 0–4w, 5–8w, 8w+ (the backend's aging4w / aging8w / aging12w, "the rest"; FreshnessService::classRollup for a
 * class). A split of 9–12w and 12w+, or buckets over time, would need new data, so the existing buckets are used. Bar heights are shares of the
 * largest bucket; at cost the dollars are not shown (the aging dollars are retail), only the shares of on hand.
 */
export const RECEIPT_TIMING_LABELS: Record<(typeof BUCKETS)[number]['key'], string> = { aging4wRetail: '0–4w', aging8wRetail: '5–8w', aging12wRetail: '8w+' };
export const ReceiptTimingChart: React.FC<{ freshness: Freshness; showDollars?: boolean }> = ({ freshness, showDollars = true }) => {
  const bucketSum = BUCKETS.reduce((a, b) => a + freshness[b.key], 0);
  const total = isPositive(freshness.onHandRetail) ? freshness.onHandRetail : bucketSum;
  if (!isPositive(total)) return null;
  const parts = BUCKETS.map((b) => ({ ...b, label: RECEIPT_TIMING_LABELS[b.key], dollars: freshness[b.key], share: freshness[b.key] / total }));
  const max = Math.max(...parts.map((p) => p.dollars), 0);
  const name = `On-hand inventory by inflow age: ${parts.map((p) => `${p.label} ${showDollars ? fmtCurrency(p.dollars) : fmtPct(p.share)}`).join(', ')}`;
  return (
    <div className="flex flex-col gap-1" data-testid="receipt-timing">
      <h5 title="On-hand inventory by inflow age" className="text-[12px] font-semibold text-[#344054]">Inflow timing</h5>
      <div role="img" aria-label={name} className="flex items-end gap-3 h-[72px]">
        {parts.map((p) => (
          <div key={p.key} className="flex flex-col items-center justify-end gap-1 h-full w-14">
            <span className="text-[11px] font-medium tabular-nums text-[#475467]">{showDollars ? fmtCurrency(p.dollars) : fmtPct(p.share)}</span>
            <div data-bucket={p.label} className={`w-full rounded-t ${p.color}`} style={{ height: `${max > 0 ? Math.max(2, (p.dollars / max) * 40) : 2}px` }} />
          </div>
        ))}
      </div>
      <ul className="flex gap-3 text-[11px] font-medium text-[#667085]" aria-hidden="true">
        {parts.map((p) => <li key={p.key} className="w-14 text-center">{p.label}</li>)}
      </ul>
    </div>
  );
};

export interface InventoryHealthRowDetailProps {
  model: WeeklyPlanningReportModel;
  flatRow: FlatRow;
}

/** The inventory strip (see the header). */
export const inventoryStats = (row: AnyRow, ctx: CellContext): Stat[] => {
  const c = row.periods[String(ctx.targetPeriod)];
  const text = (id: string): string => metricById(id).cell(row, ctx).text;
  const startRetail = inventoryRetailOf(row, ctx);
  const plan = c?.inventoryPlan ?? null;
  const vsPlan = isNum(startRetail) && isNum(plan) ? pctChange(startRetail, plan) : null;
  const ly = lyBomOf(row, ctx);
  const lyMissing = !isNum(ly) && lyBomMissing(row, ctx);
  const bomInfo = [metricById('inventory').cell(row, ctx).title, c?.isEstimated ? `Values in italics are ${ESTIMATED_TOOLTIP}.` : null].filter((t, i, all): t is string => !!t && all.indexOf(t) === i).join(' ');
  const common = summaryStats(row, ctx);
  const pick = (id: string): Stat[] => common.filter((st) => st.id === id);
  return [
    { id: 'inventory', label: bomInventoryLabel(ctx.grain), value: text('inventory'), info: bomInfo || null },
    { id: 'inventoryEnd', label: 'End of period', value: basisText(inventoryEndOnBasis(row, ctx), fmtCurrency).text },
    { id: 'inventoryPlan', label: `Plan ${bomAbbr(ctx.grain)}`, value: text('inventoryPlan'), info: ctx.grain === 'monthly' && c?.planV2History ? v2HistoryNote(ctx.targetPeriod) : null },
    { id: 'vsPlan', label: 'vs Plan', value: signedPct(vsPlan) },
    coverVsTarget(row, ctx),
    // LY BOM (LY month M−1's EOM) against this period's start: BOM compares only with BOM.
    ...(isNum(ly) ? [{ id: 'ly', label: 'LY BOM', value: text('inventoryLy'), sub: isNum(startRetail) ? `${signedPct(pctChange(startRetail, ly))} vs LY BOM` : null }] : []),
    ...(lyMissing ? [{ id: 'ly', label: 'LY BOM', value: EMPTY, info: LY_BOM_MISSING_NOTE }] : []),
    ...pick('onOrder'),
    ...pick('received'),
  ];
};

export const InventoryHealthRowDetail: React.FC<InventoryHealthRowDetailProps> = ({ model, flatRow }) => {
  const { ctx, capabilities } = model;
  if (!ctx) return null;
  const { row } = flatRow;
  const unit = coverUnit(ctx.grain);
  const text = (id: string, r: AnyRow): string => metricById(id).cell(r, ctx).text;
  // the aging card follows the data and capability only (the Freshness switch hides the columns, not the card).
  const showAging = agingShown(capabilities, true);
  const freshness = row.freshness ?? null; // vendor, SKU and product rows; class rows carry the rollup from their SKUs
  const ahead = salesAhead([row], salesAheadPeriods(ctx.targetPeriod, model.payload?.otbPeriods ?? model.payload?.spanPeriods), ctx.grain);

  const inLocationView = model.view.state.view === 'locationCategoryClass' && !isVendorRow(row) && !!row.classCode;
  const locations = inLocationView
    ? model.totalsRows.filter((f) => (f.kind === 'leaf' || f.kind === 'group') && !isVendorRow(f.row) && f.row.classCode === row.classCode)
    : [];
  const th = (t: string, left = false) => <th className={cn(DENSE_TH_CSS, left ? 'text-left pr-3' : 'text-right pl-3')}>{t}</th>;
  const td = (t: React.ReactNode, left = false) => <td className={cn(DENSE_TD_CSS, left ? 'text-left pr-3 whitespace-nowrap' : 'text-right pl-3')}>{t}</td>;

  return (
    <div className="flex flex-col gap-3 py-2">
      <StatStrip label="Inventory" stats={inventoryStats(row, ctx)} />
      <div className="flex flex-wrap items-start gap-3">
        {showAging && (
          <DetailCard title="Aging" className="w-[300px]">
            {freshness ? <><AgingBar freshness={freshness} showDollars={ctx.basis !== 'cost'} /><ReceiptTimingChart freshness={freshness} showDollars={ctx.basis !== 'cost'} /></> : <p className="text-[11px] font-medium text-[#667085]">No SKU stock to age</p>}
          </DetailCard>
        )}
        {ahead.data.some((d) => d.plan !== null) && <SalesAheadChart dense ahead={ahead} grain={ctx.grain} width={DETAIL_CHART_WIDTH} height={DETAIL_CHART_HEIGHT} />}
        {/* the class's benchmark metrics against its master class (nothing for vendor / product rows, below class level or at none). */}
        <BenchmarkDetail row={row} ctx={ctx} metrics={['weeksOfStock', 'stockToSales', 'sellThrough', 'turnover', 'markdownPct']} />
        {/* Item 7 (Wendi, 2026-10-22): the figures behind a product's suggested reorder. */}
        <ReorderDetail row={row} ctx={ctx} id={flatRow.id} />
        <FamilyDrivers row={row} rows={row.key.startsWith('chain:') ? chainChildRows(model.payload?.rows ?? [], ctx, model.view.state.filters.locations, row.key) : model.payload?.rows ?? []} ctx={ctx} id={flatRow.id} />
        {locations.length > 0 && (
          <DetailCard title="By location" headingId={`by-location-${flatRow.id}`}>
            <table aria-labelledby={`by-location-${flatRow.id}`} className={DENSE_TABLE_CSS}>
              <thead>
                <tr>{th('Location', true)}{th(bomInventoryLabel(ctx.grain))}{th('Plan')}{th('vs Plan')}{th(unit)}</tr>
              </thead>
              <tbody>
                <ShowAllRows
                  items={locations}
                  colSpan={5}
                  render={(f) => {
                    const r = f.row as ClassRow;
                    const rc = r.periods[String(ctx.targetPeriod)];
                    const invRetail = inventoryRetailOf(r, ctx);
                    const rPlan = rc?.inventoryPlan ?? null;
                    return (
                      <tr key={f.id}>
                        {td(r.locationName ?? r.locationCode ?? EMPTY, true)}
                        {td(text('inventory', r))}
                        {td(text('inventoryPlan', r))}
                        {td(isNum(invRetail) && isNum(rPlan) ? signedPct(pctChange(invRetail, rPlan)) : EMPTY)}
                        {td(r.summary ? fmtCover(r.summary.wos, resolveTarget(r.summary, ctx.wosOverride).target) : EMPTY)}
                      </tr>
                    );
                  }}
                />
              </tbody>
            </table>
          </DetailCard>
        )}
      </div>
    </div>
  );
};



// Inventory Health's page config (Plan 4 spec 7.1). A module-level constant: the page shell
// memoises on its identity. KPIs and charts read `model.totalsRows` (the expandAll tree) through
// `totalRows`, which never counts SKU attachments, so loading SKUs never changes a KPI.
//
// Decisions recorded here:
// - "Total Inventory" compares with plan over the rows that have a plan (Σ inventory ÷ Σ plan over the
// same rows, so an unplanned class never reads as on target), and with Compare = LY against last
// year's INVENTORY (`inventoryLy`, monthly only), never sales (spec 7.1 "also fixed", SPS-5541 3.3).
// `inventoryLy` is last year's BOM (LY month M−1's EOM), so start compares with start. Weekly has
// no LY week-start inventory: the sub-line says so rather than pair a month figure with a week.
// Plans and LY are retail, so the sub-line sums the RETAIL inventory (`inventoryRetailOf`) on every
// basis; the KPI value shows the displayed basis.
// - The fourth KPI is "Aging 8w+" (Σ aging12wRetail ÷ Σ onHandRetail, the share of on-hand dollars received more than 8 weeks ago)
// and the second dashboard card "Aging by class" when the kit's `agingShown` holds (A/B with inflows; the
// Freshness switch hides the freshness columns only, so the KPI and card never flip with it) AND the
// counted rows carry freshness: the vendor rows, and the class rows, whose buckets the backend rolls up
// from their SKUs (inventory $ in each bucket ÷ class inventory), so the class views need no SKU load. Otherwise "Avg Fresh %"
// (Σ lookback inflow ÷ Σ current on-hand over the rows classFreshPct can compute, capped at 100%) and "Freshness by class":
// a client with no SKU data or inflows, or a class with no SKU stock, falls back rather than show an empty aging figure.
// - Compact ("$ Detail / Compact", `state.inventoryDetail`) removes the plan inventory, vs plan and
// cost inventory columns from the table, the Columns menu, How it works and the CSV. It is a deny-list,
// so the kit's Sales Trend / Trend % columns (default-visible in a trend period only,
// after the cover column) stay in Compact.
// - the hierarchy list, product rows and the trend are the kit's; KPIs and cards keep
// counting the class (or vendor) rows of `totalsRows` in the product views, so loading products never
// moves a KPI, and a product row's detail reads the freshness the backend rolled up for it.
// - Benchmarks: Avg WOS / Avg MOS carries a benchmark line (the client's benchmark weeks of stock vs the
// vertical's G4, in the report's unit) and Total Inventory a stock-to-sales line; Overstocked / Understocked counts and
// Fresh % / Aging 8w+ measure nothing a benchmark does (Fresh % is inflows over on-hand, not sell-through) and carry none.
// Both lines read `ctx.benchmarks.client` only (class rows + benchmark payload, never SKU / product rows), so they never
// move while those load; absent at entitlement 'none', "peers: not enough peers" when the pool cannot be published. A ratio
// is unitless, so the Total Inventory line stays on the cost basis. The WOS chart gets the G3 "Peers" marker (class level,
// class-first views); the aging / fresh chart none; the dashboard ends with "You vs peers".

// "vs Plan $" (the deviation in dollars) is plan detail too, so Compact hides it with vs Plan.
export const COMPACT_HIDDEN_COLUMNS: readonly string[] = ['inventoryPlan', 'inventoryVsPlan', 'inventoryVsPlanDollars'];

const coverUnitOf = (ctx: CellContext): 'WOS' | 'MOS' => (ctx.grain === 'monthly' ? 'MOS' : 'WOS');

/** Σ retail inventory ÷ Σ baseline − 1 over the counted rows that have the baseline (plan or LY inventory, both retail). */
const inventoryVs = (rows: FlatRow[], ctx: CellContext, field: 'inventoryPlan' | 'inventoryLy'): number | null => {
  let own = 0;
  let base = 0;
  let any = false;
  rows.forEach((f) => {
    // LY BOM is Monthly only (lyBomOf): a weekly inventoryLy is never paired with a week's start.
    const b = field === 'inventoryLy' ? lyBomOf(f.row, ctx) : f.row.periods[String(ctx.targetPeriod)]?.[field];
    const inv = inventoryRetailOf(f.row, ctx);
    if (!isNum(b) || !isNum(inv)) return;
    own += inv;
    base += b;
    any = true;
  });
  return any ? pctChange(own, base) : null;
};

/**
 * Σ aging12wRetail ÷ Σ onHandRetail over the counted rows with freshness (vendor rows, and class rows with the buckets the backend
 * rolls up from their SKUs); null when there are none. Counted rows are each class once, so a parent and its children never both count.
 */
export const aging12Share = (rows: FlatRow[]): number | null => {
  const withFreshness = rows.map((f) => f.row.freshness ?? null).filter((x): x is NonNullable<typeof x> => !!x);
  if (withFreshness.length === 0) return null;
  return ratioPct(withFreshness.map((x) => x.aging12wRetail), withFreshness.map((x) => x.onHandRetail));
};

/**
 * Σ lookback inflow ÷ Σ current on-hand (inventoryEnd of the current period), capped at 100%, over the
 * rows the kit's `classFreshPct` can compute (inflow known, on-hand positive), so the KPI agrees with
 * the Fresh % column and the freshness chart.
 */
export const freshShare = (rows: FlatRow[], ctx: CellContext): number | null => {
  const current = String(ctx.currentPeriod ?? ctx.targetPeriod);
  const inflows: number[] = [];
  const onHand: number[] = [];
  rows.forEach((f) => {
    const inv = f.row.periods[current]?.inventoryEnd;
    const inflow = sumCells(f.row.periods, 'inflow', ctx.lookback);
    if (!isNum(inv) || !isPositive(inv) || inflow === null) return;
    onHand.push(inv);
    inflows.push(inflow);
  });
  const v = ratioPct(inflows, onHand);
  return v === null ? null : Math.min(1, v);
};

/**
 * The kit's aging rule (A/B with inflows) AND the counted rows carry freshness (the vendor views). the KPI and the
 * dashboard card follow the data and capability only; the Freshness switch hides the freshness COLUMNS, never the aging figure.
 */
const agingShare = (m: WeeklyPlanningReportModel): number | null =>
  agingShown(m.capabilities, true) ? aging12Share(totalRows(m.totalsRows)) : null;

const BM_TITLE = (ctx: CellContext): string => ctx.benchmarks?.label ?? '';

/** Where your benchmark value comes from: the rows the report counts, or (vendor / product views) the client level, which the text and tooltip then say. */
const scopeNote = (scope: 'rows' | 'client'): string => (scope === 'client' ? 'Client level: every class passing the scope filters, not only the rows shown.' : 'Your value covers the same classes as the other KPIs.');

/** Avg WOS / MOS benchmark line: "peers 20.0 · you 14.8 in Sep 2026 month-end (index 0.74)"; the cover unit follows the report's grain. */
export const wosBenchmarkLine = (ctx: CellContext, flatRows: FlatRow[]): KpiBenchmarkLine | undefined => {
  const b = ctx.benchmarks;
  const r = benchmarkForRows(flatRows, ctx);
  if (!b || !r) return undefined;
  const c = r.value;
  if (!c.peer?.publishable) return { text: `peers: ${PEER_TEXT_NOT_ENOUGH} · ${BM_TITLE(ctx)}` };
  const view: BenchmarkView = { grain: ctx.grain, weeksInMonth: b.weeksInMonth, basis: ctx.basis };
  const peerWos = gatedPeerValue(c.peer, 'weeksOfStock');
  const idx = benchmarkIndex('weeksOfStock', c.own.weeksOfStock, peerWos);
  return {
    text: `peers ${peerIn(c.peer, 'weeksOfStock', view)} · you ${ownIn('weeksOfStock', c.own.weeksOfStock, view)} in ${benchmarkMonthEnd(b)}${ctx.basis === 'cost' ? ' (retail)' : ''} (index ${formatIndex('weeksOfStock', idx)}${r.scope === 'client' ? ', client level' : ''})`,
    tooltip: retailTooltip(`${c.peerLabel}. Benchmark ${ctx.grain === 'monthly' ? 'months' : 'weeks'} of stock (month-end inventory ÷ ${ctx.grain === 'monthly' ? "the month's sales" : 'sales per week'}), not this report's ${ctx.grain === 'monthly' ? 'MOS' : 'WOS'}. ${scopeNote(r.scope)}`, ctx.basis),
    tone: indexTone('weeksOfStock', idx) === 'bad' ? 'warn' : 'neutral',
  };
};

/** Total Inventory benchmark line: "stock-to-sales you 2.74 · peers 4.66 (index 0.59) · Sep 2026 month-end". */
export const stockToSalesBenchmarkLine = (ctx: CellContext, flatRows: FlatRow[]): KpiBenchmarkLine | undefined => {
  const b = ctx.benchmarks;
  const r = benchmarkForRows(flatRows, ctx);
  if (!b || !r) return undefined;
  const c = r.value;
  if (!c.peer?.publishable) return { text: `peers: ${PEER_TEXT_NOT_ENOUGH} · ${BM_TITLE(ctx)}` };
  const idx = benchmarkIndex('stockToSales', c.own.stockToSales, gatedPeerValue(c.peer, 'stockToSales'));
  return {
    text: `${retailTag('stock-to-sales', ctx.basis)} you ${ownIn('stockToSales', c.own.stockToSales)} · peers ${peerIn(c.peer, 'stockToSales')} (index ${formatIndex('stockToSales', idx)}${r.scope === 'client' ? ', client level' : ''}) · ${benchmarkMonthEnd(b)}`,
    tooltip: retailTooltip(`${c.peerLabel}. Benchmark stock-to-sales: beginning inventory ÷ the month's sales. ${scopeNote(r.scope)}`, ctx.basis),
    tone: indexTone('stockToSales', idx) === 'bad' ? 'warn' : 'neutral',
  };
};

const kpis = (m: WeeklyPlanningReportModel): KpiItem[] => {
  const { ctx } = m;
  if (!ctx) return [];
  const { state } = m.view;
  const counted = totalRows(m.totalsRows);
  const t = kpiTotals(m.totalsRows, ctx);
  const a = t.byStatusActionable;
  const actionableNoun = totalsNoun(state.view, t.rowsActionable);
  const { cover, target } = weightedCover(m.totalsRows, ctx);
  const ly = state.compare === 'ly';
  const vs = inventoryVs(counted, ctx, ly ? 'inventoryLy' : 'inventoryPlan');
  let invSub: string;
  if (vs !== null) invSub = `${signedPct(vs)} vs ${ly ? 'LY BOM' : 'plan'}`;
  else if (ly) invSub = ctx.grain === 'weekly' ? 'No LY week-start inventory' : counted.some((f) => lyBomMissing(f.row, ctx)) ? LY_BOM_MISSING_KPI : 'No LY BOM inventory';
  else invSub = 'No plan inventory';
  const invTooltip = ly ? (ctx.grain === 'weekly' ? LY_BOM_WEEKLY_TOOLTIP : LY_BOM_TOOLTIP) : undefined;
  const overridden = isNum(ctx.wosOverride) && ctx.wosOverride > 0;
  const aging = agingShare(m);
  const fourth: KpiItem = aging !== null
    ? { label: 'Aging 8w+', value: fmtPct(aging), sub: 'of on-hand dollars received more than 8 weeks ago' }
    : { label: 'Avg Fresh %', value: fmtPct(freshShare(counted, ctx)), sub: 'lookback inflows ÷ current on-hand' };
  return [
    // the total on the basis; at cost "—" with a note when a counted row has no cost inventory (Tier C).
    t.notAtCost.includes('inventory')
      ? { label: 'Total Inventory', value: fmtCurrency(null), sub: 'Not available at cost', tooltip: NOT_AT_COST_TOOLTIP, benchmark: stockToSalesBenchmarkLine(ctx, m.totalsRows) }
      : { label: 'Total Inventory', value: fmtCurrency(t.inventory), sub: invSub, ...(invTooltip ? { tooltip: invTooltip } : {}), benchmark: stockToSalesBenchmarkLine(ctx, m.totalsRows) },
    { label: `Avg ${coverUnitOf(ctx)}`, value: fmtWeeks(cover, coverCap(ctx.grain)), sub: target === null ? 'no plan target' : `vs ${fmtWeeks(target, coverCap(ctx.grain))} ${overridden ? 'target' : 'plan'}`, benchmark: wosBenchmarkLine(ctx, m.totalsRows) },
    // no-action class types (PNOTB, NOTB, History, Distress, Warehouse) are left out of both counts (M1 §8).
    { label: 'Overstocked', value: String(a.overstocked), sub: `of ${t.rowsActionable} ${actionableNoun}`, tone: a.overstocked > 0 ? 'warn' : 'neutral' },
    // (QA P4-8): Understocked beside Overstocked; out of stock is counted with it.
    { label: 'Understocked', value: String(a.understocked + a.out_of_stock), sub: `of ${t.rowsActionable} ${actionableNoun}${a.out_of_stock > 0 ? ` · ${a.out_of_stock} out of stock` : ''}`, tone: a.understocked + a.out_of_stock > 0 ? 'bad' : 'neutral' },
    fourth,
  ];
};

const coverSeries = (unit: string): ChartSeries[] => [
  { key: 'value', label: unit, kind: 'bar', color: '#9E77ED' },
  { key: 'plan', label: 'Plan target', kind: 'line', color: '#101828', bold: true },
];
const AGING_SERIES: ChartSeries[] = [{ key: 'value', label: '8w+ share', kind: 'bar', color: '#F79009' }];
const FRESH_SERIES: ChartSeries[] = [{ key: 'value', label: 'Fresh %', kind: 'bar', color: '#12B76A' }];

const dashboardCards = (m: WeeklyPlanningReportModel): React.ReactNode[] => {
  const { ctx } = m;
  if (!ctx) return [];
  const unit = coverUnitOf(ctx);
  const view = m.view.state.view;
  const second = agingShare(m) !== null ? (
    <ChartCard
      key="aging"
      title="Aging by class"
      caption="Share of on-hand dollars received more than 8 weeks ago"
      data={chartRowsByClass(m.totalsRows, ctx, 'aging12', view)}
      series={AGING_SERIES}
      xKey="name"
      sortable
      formatValue={fmtPct}
    />
  ) : (
    <ChartCard key="fresh" title="Freshness by class" caption="Lookback inflows ÷ current on-hand" data={chartRowsByClass(m.totalsRows, ctx, 'fresh', view)} series={FRESH_SERIES} xKey="name" sortable formatValue={fmtPct} />
  );
  return [
    <ChartCard
      key="cover"
      title={`${unit} vs plan by class`}
      caption="Bars: current cover · line: plan target"
      data={chartRowsByClassWithPeers(m.totalsRows, ctx, 'cover', view, 'weeksOfStock')}
      series={withPeers(coverSeries(unit), ctx, view)}
      note={peerMarkerNote(ctx, view) ?? undefined}
      xKey="name"
      sortable
      formatValue={fmtWeeks}
      cap={coverCap(ctx.grain)}
    />,
    second,
    benchmarkDashboardCard(m, ['weeksOfStock', 'stockToSales', 'sellThrough', 'turnover', 'markdownPct']),
  ].filter((card) => card !== null);
};

type InventoryDetail = 'detail' | 'compact';
const DETAIL_OPTIONS: { value: InventoryDetail; label: string }[] = [{ value: 'detail', label: '$ Detail' }, { value: 'compact', label: 'Compact' }];

export const INVENTORY_HEALTH_CONFIG: ReportPageConfig = {
  reportKey: 'inventory_health',
  title: 'Inventory Health',
  //: the stock-position report (BOM vs plan, cover, on order, aging); Weekly Action says what to buy.
  subtitle: 'Your stock position by class: BOM against plan, cover, on order and aging.',
  icon: <HeartPulse className="w-5 h-5" aria-hidden="true" />,
  howItWorksIntro:
    'Inventory Health shows your stock position: whether each class holds the right amount of stock, what is on order, and how old the stock is. It compares BOM inventory (what you have at the start of the period) with plan, and the weeks (or months) of stock you hold with the target. For what to buy, delay or cancel, or sell down, use Inventory Action. ' +
    // name the rule (plan BOM × the multiplier), no history note.
    'A class at or below its plan inventory is never Overstocked (it shows Watch – high instead); one above plan BOM × the Overstock at multiplier (1.5× by default) is always Overstocked. Watch – low and Watch – high flag classes close to the understocked or overstocked edge. ' +
    'Stock with no sales in the lookback reads No recent sales; a Deadstock tag marks a class or product with more than a year of stock that is selling less than last year, and you can filter on it from Status. ' +
    'Compare = LY measures inventory against last year (Monthly only). Fresh % and the aging columns show how recently stock arrived: Aging 8w+ is the share of on-hand dollars received more than 8 weeks ago, and each class shows its aging without loading SKUs (the dollars in each bucket ÷ the class\'s inventory, parents summing their children). ' +
    'Open a row for its inventory summary, aging and locations. The Trended sales and % of period elapsed columns are available from Columns while the period is still in progress. ' +
    'Benchmarks compare you with peers for one complete month: Avg WOS and Total Inventory carry a peer line, the weeks-of-stock chart a peer marker, and the dashboard a "You vs peers" card. Overstocked, Understocked and Fresh % / Aging have no benchmark; see Benchmarks below.',
  kpis,
  dashboardCards,
  extraFilterControls: (m) => (
    <SegmentedControl<InventoryDetail>
      ariaLabel="Inventory detail"
      size="sm"
      options={DETAIL_OPTIONS}
      value={m.view.state.inventoryDetail}
      onChange={(inventoryDetail) => m.view.set({ inventoryDetail })}
    />
  ),
  columnsFilter: (m, ids) => (m.view.state.inventoryDetail === 'compact' ? ids.filter((id) => !COMPACT_HIDDEN_COLUMNS.includes(id)) : ids),
  showFreshnessToggle: true,
  renderRowDetail: (m, flatRow) => <InventoryHealthRowDetail model={m} flatRow={flatRow} />,
};

// Inventory Health (spec 7.1), rebuilt on the kit (Plan 4 ): the shared page shell
// with this report's config. The exported names and props are the ones the two mounting pages use
// (ClientAnalytics, ClientDashboardV3Reports).

export interface InventoryHealthReportProps {
  clientId: string | number;
  epochMonth: number | null;
  setEpochMonth?: (epochMonth: number) => void;
}

export const InventoryHealthReport: React.FC<InventoryHealthReportProps> = (props) => <WeeklyPlanningReportPage config={INVENTORY_HEALTH_CONFIG} {...props} />;

export interface InventoryHealthReportModalProps {
  clientId: string | number;
  epochMonth: number | null;
  onClose: () => void;
}

export const InventoryHealthReportModal: React.FC<InventoryHealthReportModalProps> = ({ clientId, epochMonth, onClose }) => (
  <ReportModal onClose={onClose}>
    <InventoryHealthReport clientId={clientId} epochMonth={epochMonth} />
  </ReportModal>
);

export default InventoryHealthReport;
